import type { BootstrapConfig } from '../api/types';
import type { BuilderStep } from '../types/models';
import type { BuilderStore } from '../state/BuilderStore';
import type { LayerColorSelection } from '../state/types';
import type { ThreeJsViewer } from '../image/ThreeJsViewer';
import { GradientMapCompositor, type GradientLayer } from '../image/GradientMapCompositor';
import type { LayerDefinition } from '../image/LayerCompositor';
import { fetchModelDetail, resolveImageUrl } from '../api/client';
import { formatCurrency } from '../pricing/PriceFormatter';
import { parseSettings } from '../config/BuilderConfig';

/** One source image for a specific layer type (or a static non-colorable layer). */
interface ColorLayerRecord {
    id: number;
    label: string;
    layer_type_id: number | null;   // null = static layer drawn as-is
    layer_type_name: string | null;
    image_path: string;
    render_mode: 'gradient' | 'multi_image';
    z_index: number;
    sort_order: number;
}

/** A selectable palette color assigned to a layer type. */
interface LayerColorRecord {
    id: number;
    color_id: number;
    name: string;
    hex_value: string;
    image_path: string | null;  // per-color image for multi_image mode
    msrp_price: number;
    sale_price: number;
    dealer_net: number;
    is_default: boolean;
    sort_order: number;
}

/** Color mapping on a 3D mesh part */
interface ThreeDPartColorMapping {
    id: number;
    color_layer_type_id: number;
    color_layer_type_name: string;
}

/** Lightweight 3D part record (subset of ThreeDPart from types.ts) */
interface ThreeDPartLocal {
    id: number;
    mesh_name: string;
    label: string;
    default_visible: boolean | number;
    sort_order: number;
    color_mappings: ThreeDPartColorMapping[];
}

/**
 * Step 3 – Boat Color.
 *
 * Shows one color-swatch group per structural layer type (hull, canvas, etc.).
 * Each layer has ONE grayscale source image; colors are applied at run-time via
 * the GradientMapCompositor canvas algorithm.
 */
export class StepBoatColor implements BuilderStep {
    readonly id = 'boat-color';
    readonly label = 'Color';

    private container: HTMLElement | null = null;
    private canvas: HTMLCanvasElement | null = null;
    private overlayCanvas: HTMLCanvasElement | null = null;
    private compositor: GradientMapCompositor | null = null;
    private colorLayers: ColorLayerRecord[] = [];
    private layerColors: Record<string, LayerColorRecord[]> = {};
    private is3dModel = false;
    private model3dParts: ThreeDPartLocal[] = [];
    private _previewVersion = 0;

    constructor(
        private store: BuilderStore,
        private config: BootstrapConfig,
        private sharedCanvas: HTMLCanvasElement | null = null,
        private sharedOverlayCanvas: HTMLCanvasElement | null = null,
        private getViewer: (() => ThreeJsViewer | null) | null = null,
    ) { }

    isEnabled(_config: BootstrapConfig): boolean {
        return true;
    }

    async render(container: HTMLElement): Promise<void> {
        this.container = container;
        const modelId = this.store.getState().selectedModelId;

        if (!modelId) {
            container.innerHTML = `<div class="vc-step"><p class="vc-warning">No model selected.</p></div>`;
            return;
        }

        container.innerHTML = `
      <div class="vc-step vc-step-boat-color">
        <div class="vc-color-picker">
          <div id="vc-layer-sections"></div>
        </div>
      </div>
    `;

        // Use the shared canvas from the persistent left panel
        this.canvas = this.sharedCanvas;
        this.overlayCanvas = this.sharedOverlayCanvas;
        this.compositor = new GradientMapCompositor();

        try {
            const detail = (await fetchModelDetail(modelId)) as {
                color_layers: ColorLayerRecord[];
                layer_colors: Record<string, LayerColorRecord[]>;
                render_type?: string;
                model_3d_parts?: ThreeDPartLocal[];
            };

            this.is3dModel = (detail.render_type ?? 'image') === '3d';
            this.model3dParts = detail.model_3d_parts ?? [];
            this.colorLayers = detail.color_layers ?? [];
            this.layerColors = detail.layer_colors ?? {};

            this.applyDefaults();
            this.renderLayerSections();
            await this.updatePreview();
        } catch {
            container.querySelector('.vc-color-picker')!.innerHTML =
                `<p class="vc-error">Failed to load colors.</p>`;
        }
    }

    validate(): boolean {
        return true; // color selection is optional
    }

    onEnter(): void { }
    onLeave(): void { }

    destroy(): void {
        this.compositor = null;
        this.canvas = null;
        this.overlayCanvas = null;
        this.container = null;
        this.colorLayers = [];
        this.layerColors = {};
        this.model3dParts = [];
        this.is3dModel = false;
    }

    // ── private ──────────────────────────────────────────────────────────────

    /** Auto-select the default color for each colorable layer type if nothing is already selected. */
    private applyDefaults(): void {
        const current = this.store.getState().selectedColors;
        const updates: Record<string, LayerColorSelection> = {};
        const seenTypes = new Set<number>();

        // For 3D models, scan part color_mappings instead of color_layers
        const sourceEntries: Array<{ typeId: number; typeName: string }> = this.is3dModel
            ? this.model3dParts.flatMap(p => p.color_mappings.map(m => ({ typeId: m.color_layer_type_id, typeName: m.color_layer_type_name })))
            : this.colorLayers
                .filter(l => l.layer_type_id !== null)
                .map(l => ({ typeId: l.layer_type_id as number, typeName: l.layer_type_name ?? l.label }));

        for (const { typeId, typeName } of sourceEntries) {
            if (seenTypes.has(typeId)) continue;
            seenTypes.add(typeId);

            const key = typeId.toString();
            if (current[key]) continue;

            const choices = this.layerColors[key] ?? [];
            const def = choices.find(c => c.is_default) ?? choices[0];
            if (def) {
                const fakeLayer: ColorLayerRecord = {
                    id: 0, label: typeName, layer_type_id: typeId,
                    layer_type_name: typeName, image_path: '', render_mode: 'gradient',
                    z_index: 0, sort_order: 0,
                };
                updates[key] = this.makeSelection(def, fakeLayer);
            }
        }

        if (Object.keys(updates).length > 0) {
            this.store.setState({
                selectedColors: { ...current, ...updates },
            });
        }
    }

    private renderLayerSections(): void {
        const sectionsEl = this.container?.querySelector('#vc-layer-sections');
        if (!sectionsEl) return;

        // For 3D models build sections from part color_mappings; for image models from color_layers
        const seenTypes = new Set<number>();
        const colorableLayers: ColorLayerRecord[] = [];

        if (this.is3dModel) {
            for (const part of this.model3dParts) {
                for (const mapping of part.color_mappings) {
                    if (seenTypes.has(mapping.color_layer_type_id)) continue;
                    seenTypes.add(mapping.color_layer_type_id);
                    colorableLayers.push({
                        id: 0, label: mapping.color_layer_type_name,
                        layer_type_id: mapping.color_layer_type_id,
                        layer_type_name: mapping.color_layer_type_name,
                        image_path: '', render_mode: 'gradient', z_index: 0, sort_order: 0,
                    });
                }
            }
        } else {
            for (const layer of this.colorLayers) {
                if (layer.layer_type_id === null) continue;
                if (seenTypes.has(layer.layer_type_id)) continue;
                seenTypes.add(layer.layer_type_id);
                colorableLayers.push(layer);
            }
        }

        if (colorableLayers.length === 0) {
            sectionsEl.innerHTML = `<p class="vc-empty">No color options available for this model.</p>`;
            return;
        }

        sectionsEl.innerHTML = colorableLayers
            .map(layer => {
                const key = layer.layer_type_id!.toString();
                const choices = this.layerColors[key] ?? [];
                const selected = this.store.getState().selectedColors[key];

                const swatches = choices
                    .map(
                        c => `
            <button
              class="vc-swatch${selected?.colorId === c.color_id ? ' selected' : ''}"
              data-layer-type-id="${layer.layer_type_id}"
              data-color-id="${c.color_id}"
              data-assignment-id="${c.id}"
              title="${c.name}"
              style="background-color: ${c.hex_value || '#ccc'};"
            ></button>`
                    )
                    .join('');

                const info = selected
                    ? this.colorInfoHtml(selected)
                    : '';

                return `
          <div class="vc-layer-section" data-layer-type-id="${layer.layer_type_id}">
            <h3 class="vc-layer-title">${layer.layer_type_name ?? layer.label}</h3>
            <div class="vc-color-swatches">${swatches}</div>
            <div class="vc-color-info">${info}</div>
          </div>`;
            })
            .join('');

        sectionsEl.querySelectorAll('.vc-swatch').forEach(btn => {
            btn.addEventListener('click', () => {
                const el = btn as HTMLElement;
                const ltId = parseInt(el.dataset.layerTypeId ?? '0', 10);
                const colorId = parseInt(el.dataset.colorId ?? '0', 10);
                const assignId = parseInt(el.dataset.assignmentId ?? '0', 10);
                void this.selectColor(ltId, colorId, assignId);
            });
        });
    }

    private async selectColor(layerTypeId: number, colorId: number, assignmentId: number): Promise<void> {
        const key = layerTypeId.toString();
        const choices = this.layerColors[key] ?? [];
        const color = choices.find(c => c.color_id === colorId && c.id === assignmentId);
        let layer = this.colorLayers.find(l => l.layer_type_id === layerTypeId);

        // For 3D models there are no color_layers entries — synthesize a fake one for makeSelection()
        if (!layer && this.is3dModel) {
            for (const part of this.model3dParts) {
                const mapping = part.color_mappings.find(m => m.color_layer_type_id === layerTypeId);
                if (mapping) {
                    layer = {
                        id: 0, label: mapping.color_layer_type_name,
                        layer_type_id: layerTypeId, layer_type_name: mapping.color_layer_type_name,
                        image_path: '', render_mode: 'gradient', z_index: 0, sort_order: 0,
                    };
                    break;
                }
            }
        }

        if (!color || !layer) return;

        const settings = parseSettings(this.config);
        const selection = this.makeSelection(color, layer);

        this.store.setState({
            selectedColors: { ...this.store.getState().selectedColors, [key]: selection },
        });

        // Update swatch highlight for this section
        const section = this.container?.querySelector(`.vc-layer-section[data-layer-type-id="${layerTypeId}"]`);
        if (section) {
            section.querySelectorAll('.vc-swatch').forEach(s => s.classList.remove('selected'));
            section.querySelector(`.vc-swatch[data-color-id="${colorId}"]`)?.classList.add('selected');

            const infoEl = section.querySelector('.vc-color-info');
            if (infoEl) infoEl.innerHTML = this.colorInfoHtml(selection, settings);
        }

        await this.updatePreview();
    }

    private colorInfoHtml(sel: LayerColorSelection, settings = parseSettings(this.config)): string {
        return `
      <div class="vc-selected-color">
        <span class="vc-color-swatch-small" style="background-color:${sel.hexValue}"></span>
        <span class="vc-color-name">${sel.name}</span>
        <span class="vc-color-price">${sel.msrpPrice ? formatCurrency(sel.msrpPrice, settings.locale, settings.currency) : 'Included'}</span>
      </div>`;
    }

    private async updatePreview(): Promise<void> {
        // ── 3D mode: apply colors directly to Three.js mesh materials ──────────
        if (this.is3dModel) {
            const viewer = this.getViewer?.();
            if (viewer) {
                const selected = this.store.getState().selectedColors;
                for (const part of this.model3dParts) {
                    for (const mapping of part.color_mappings) {
                        const sel = selected[mapping.color_layer_type_id.toString()];
                        if (sel?.hexValue) {
                            viewer.setPartColor(part.mesh_name, sel.hexValue);
                        }
                    }
                }
            }
            return;
        }

        // ── Image mode: canvas compositing ──────────────────────────────────────
        if (!this.container || !this.canvas || !this.compositor) return;

        const version = ++this._previewVersion;
        const main = this.canvas;
        const overlay = this.overlayCanvas;
        const target = overlay ?? main;

        if (overlay) {
            // Reset any in-progress transition so rendering happens invisibly
            overlay.style.transition = 'none';
            overlay.style.opacity = '0';
        }

        await this._renderLayers(target, version);
        if (!this.container || version !== this._previewVersion) return;

        if (overlay) {
            // Cross-dissolve: fade the new frame in over the old one.
            // Only changed-layer pixels are visibly different, so the effect
            // looks like just that layer transitioning.
            void overlay.offsetWidth; // force reflow so the transition fires
            overlay.style.transition = '';
            overlay.style.opacity = '1';

            await new Promise<void>(r => setTimeout(r, 280));
            if (!this.container || version !== this._previewVersion) return;

            // Stamp the finished frame onto the main canvas, then hide overlay
            const ctx = main.getContext('2d');
            if (ctx) {
                main.width = overlay.width;
                main.height = overlay.height;
                ctx.drawImage(overlay, 0, 0);
            }
            overlay.style.transition = 'none';
            overlay.style.opacity = '0';
        }
    }

    private async _renderLayers(target: HTMLCanvasElement, version: number): Promise<void> {
        if (!this.compositor) return;

        const selected = this.store.getState().selectedColors;
        const gradientLayers: GradientLayer[] = [];
        const directLayers: LayerDefinition[] = [];

        for (const layer of this.colorLayers) {
            if (!layer.image_path && layer.render_mode !== 'multi_image') continue;

            if (layer.layer_type_id === null) {
                // Static layer — draw as-is (no color mapping)
                if (layer.image_path) {
                    gradientLayers.push({
                        src: resolveImageUrl(layer.image_path),
                        hexColor: '',
                        zIndex: layer.z_index,
                    });
                }
            } else if (layer.render_mode === 'multi_image') {
                // Multi-image mode: use the per-color image stored directly on the selection.
                // Fall back to the layer's own image_path if the color has no specific image.
                const sel = selected[layer.layer_type_id.toString()];
                const imgSrc = sel?.imagePath ?? layer.image_path ?? null;
                if (imgSrc) {
                    directLayers.push({
                        src: resolveImageUrl(imgSrc),
                        zIndex: layer.z_index,
                    });
                } else {
                    console.warn('[LayerCompositor] multi_image layer has no image src', layer);
                }
            } else {
                // Gradient mode: apply hex color via gradient-map algorithm
                const hexColor = selected[layer.layer_type_id.toString()]?.hexValue ?? '';
                gradientLayers.push({
                    src: resolveImageUrl(layer.image_path),
                    hexColor,
                    zIndex: layer.z_index,
                });
            }
        }

        if (!this.container || version !== this._previewVersion) return;

        console.debug('[LayerCompositor] rendering —', {
            gradientLayers: gradientLayers.map(l => ({ src: l.src, hex: l.hexColor, z: l.zIndex })),
            directLayers: directLayers.map(l => ({ src: l.src, z: l.zIndex })),
        });

        if (directLayers.length === 0 && gradientLayers.length > 0) {
            try {
                await this.compositor.composite(target, gradientLayers);
            } catch (err) {
                console.error('[LayerCompositor] gradient composite failed:', err);
            }
            return;
        }

        if (!this.container || version !== this._previewVersion) return;

        // Mixed mode: merge gradient + direct layers, sorted by z-index, draw sequentially.
        type DrawableEntry = { zIndex: number; type: 'gradient'; layer: GradientLayer }
            | { zIndex: number; type: 'direct'; layer: LayerDefinition };

        const drawables: DrawableEntry[] = [
            ...gradientLayers.map(l => ({ zIndex: l.zIndex, type: 'gradient' as const, layer: l })),
            ...directLayers.map(l => ({ zIndex: l.zIndex, type: 'direct' as const, layer: l })),
        ];
        drawables.sort((a, b) => a.zIndex - b.zIndex);

        // Size the target canvas to match the first drawable image
        const firstSrc = drawables[0]?.type === 'gradient'
            ? (drawables[0].layer as GradientLayer).src
            : (drawables[0]?.layer as LayerDefinition | undefined)?.src;
        if (firstSrc) {
            const refImg = await this.loadImage(firstSrc);
            if (!this.container || version !== this._previewVersion) return;
            if (target.width !== refImg.naturalWidth || target.height !== refImg.naturalHeight) {
                target.width = refImg.naturalWidth;
                target.height = refImg.naturalHeight;
            }
        }

        if (!this.container || version !== this._previewVersion) return;

        const ctx = target.getContext('2d');
        if (!ctx) return;

        ctx.clearRect(0, 0, target.width, target.height);

        for (const entry of drawables) {
            try {
                if (entry.type === 'gradient') {
                    const gl = entry.layer as GradientLayer;
                    const img = await this.loadImage(gl.src);
                    if (!this.container || version !== this._previewVersion) return;
                    if (gl.hexColor) {
                        const recolored = await this.compositor.applyGradientMap(img, gl.hexColor);
                        if (!this.container || version !== this._previewVersion) return;
                        ctx.globalAlpha = gl.opacity ?? 1;
                        ctx.drawImage(recolored, 0, 0, target.width, target.height);
                    } else {
                        ctx.globalAlpha = gl.opacity ?? 1;
                        ctx.drawImage(img, 0, 0, target.width, target.height);
                    }
                } else {
                    const dl = entry.layer as LayerDefinition;
                    const img = await this.loadImage(dl.src);
                    if (!this.container || version !== this._previewVersion) return;
                    ctx.globalAlpha = dl.opacity ?? 1;
                    ctx.drawImage(img, 0, 0, target.width, target.height);
                }
                ctx.globalAlpha = 1;
            } catch (err) {
                console.error('[LayerCompositor] failed to draw layer:', entry, err);
                ctx.globalAlpha = 1; // reset even on failure
            }
        }
    }

    /** Load an image, caching for reuse. */
    private loadImage(src: string): Promise<HTMLImageElement> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.crossOrigin = 'anonymous';
            img.onload = () => resolve(img);
            img.onerror = () => reject(new Error(`Failed to load: ${src}`));
            img.src = src;
        });
    }

    private makeSelection(color: LayerColorRecord, layer: ColorLayerRecord): LayerColorSelection {
        return {
            colorId: color.color_id,
            layerTypeId: layer.layer_type_id!,
            layerTypeName: layer.layer_type_name ?? layer.label,
            name: color.name,
            hexValue: color.hex_value,
            imagePath: color.image_path ?? null,
            msrpPrice: color.msrp_price,
            salePrice: color.sale_price,
            dealerNet: color.dealer_net,
        };
    }
}
